import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { generatePath, useNavigate } from "react-router-dom";

import Sidebar from "~/components/Sidebar";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import { INVOICE_DETAILS_PATH } from "~/constants/appRoutes.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import {
	All_PENDING_INVOICES_QUERY_KEY,
	useAllPendingInvoices,
} from "~/modules/billing/api/pendingInvoice/pendingInvoiceQueries.ts";
import { ALL_PROCESSED_INVOICES_QUERY_KEY } from "~/modules/billing/api/processedInvoice/processedInvoiceQueries.ts";
import StartBillingProcessForProjectInvoicesForm
	from "~/modules/billing/components/PendingInvoicesIndexView/components/StartBillingProcessForProjectInvoicesSidebar/StartBillingProcessForProjectInvoicesForm.tsx";
import { createPendingInvoiceTableDataFromProjectInvoice } from "~/modules/billing/utils/billingUtils.ts";
import { useAllUsers } from "~/modules/user/api/user/userQueries.ts";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	invoiceId?: string;
	userCanManageInvoices: boolean;
};

const StartBillingProcessForProjectInvoicesSidebar = ({
	isOpen,
	onClose,
	invoiceId,
	userCanManageInvoices,
}: Props) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const handleSuccess = useCallback(async () => {
		if (!invoiceId) return;
		queryClient.invalidateQueries({ queryKey: ALL_PROCESSED_INVOICES_QUERY_KEY });
		queryClient.invalidateQueries({ queryKey: All_PENDING_INVOICES_QUERY_KEY });
		navigate(generatePath(INVOICE_DETAILS_PATH, { invoiceId }));
	}, [invoiceId, navigate, queryClient]);

	const { data, options } = useAggregatedDataV2({
		allUsers: useAllUsers(),
		allPendingInvoices: useAllPendingInvoices(),
	});

	const { allPendingInvoices, allUsers } = data;

	const enrichedInvoiceData = useMemo(() => {
		if (allPendingInvoices) {
			const invoice = allPendingInvoices.projectInvoices.find(
				(inv) => inv.id === invoiceId,
			);
			if (invoice) {
				return createPendingInvoiceTableDataFromProjectInvoice(
					invoice,
					allPendingInvoices.projectPhases,
					allPendingInvoices.projects,
				);
			}
		}
		return null;
	}, [allPendingInvoices, invoiceId]);

	const isReady = isOpen && enrichedInvoiceData && allUsers && !options.isLoading;

	return (
		<Sidebar
			closeOnOutsideClick={false}
			width={"3/4"}
			open={isOpen}
			setOpen={onClose}
		>
			{options.isLoading && <SidebarBusyOverlay />}

			{isReady && (
				<StartBillingProcessForProjectInvoicesForm
					allUsersData={allUsers}
					initialInvoiceTypeId={enrichedInvoiceData.invoiceTypeId}
					onCancel={onClose}
					onSuccess={handleSuccess}
					pendingInvoiceData={enrichedInvoiceData}
					userCanManageInvoices={userCanManageInvoices}
				/>
			)}
		</Sidebar>
	);
};

export default StartBillingProcessForProjectInvoicesSidebar;